/* eslint-disable @typescript-eslint/consistent-type-definitions */
/* eslint-disable prefer-arrow/prefer-arrow-functions */
import * as React from "react";
import { useState } from "react";
import {
    Form,
    Link,
    Outlet,
    Params,
    useActionData,
    useFetcher,
    useLoaderData,
    useSubmit
} from "react-router-dom";
import {
    Breadcrumb,
    BreadcrumbDivider,
    BreadcrumbSection,
    Button, Dimmer,
    Grid,
    GridColumn,
    Header, Loader,
    Modal
} from "semantic-ui-react";
import "./project-show.css";
import { RootStore } from "../../../stores/root-store";
import { Constants } from "../../../globals/constants";
import {
    ConstructionPermitProject,
    CreationProject,
    DezoningProject,
    LandfillSiteProject,
    LandUsePlanChangeProject,
    Project,
    SoilImprovementProject,
    UsageProject
} from "../../../stores/project/project";
import { AppState } from "../../../stores/app-mobx";
import ConstructionPermitShow from "./construction-permit-show";
import DezoningShow from "./dezoning-show";
import SoilImprovementShow from "./soil-improvement-show";
import LandfillSiteShow from "./landfill-site-show";
import LandUsePlanChangeShow from "./land-use-plan-change-show";
import AreaDataCreationProject from "./area-data-creation-project";
import AreaDataUsageProject from "./area-data-usage-project";
import AreaDataLandfillSite from "./area-data-landfill-site";
import { TransportError } from "../../../stores/data-transporter";
import SVG from "react-inlinesvg";
import locateIcon from "../../../assets/images/icons/plain/map_location2.svg";
import ActionStatusIcon from "../../../components/action-status-icon/action-status-icon";
import { getLabel } from "../../../reflection/reflection";

type LoadResult = {
    project: Project;
    appStore: AppState;
}

export async function loader(
    params: Params,
    rootStore: RootStore,
    appStore: AppState
) {
    if (params.projectCategory !== undefined && params.projectId !== undefined) {
        const projectId = parseInt(params.projectId) || 0;
        const projectCategory = params.projectCategory;

        try {
            let project: Project;
            const rootStoreSession = rootStore.newSession();

            if (projectCategory === Constants.projectCategoryCreation) {
                project = await rootStoreSession.projectStore.loadCreationProject(projectId);
                await rootStoreSession.areaStore.loadCreationAreas({ projectId: projectId });
                await rootStoreSession.compensationStore.loadWithOptions({ creationProjectId: projectId });
                await rootStoreSession.projectStore.loadAllUsageProjects();
                await rootStoreSession.activityLogEntryStore.load({
                    tableName: Constants.creationProjectTableName,
                    rowId: projectId
                });
            } else {
                project = await rootStoreSession.projectStore.loadUsageProject(projectId);
                await rootStoreSession.areaStore.loadUsageAreas({ projectId: projectId });
                await rootStoreSession.projectStore.loadAllCreationProjects();
                await rootStoreSession.areaStore.loadAllCreationAreas();
                await rootStoreSession.compensationStore.loadAll();
                await rootStoreSession.activityLogEntryStore.load({
                    tableName: Constants.usageProjectTableName,
                    rowId: projectId
                });
                await rootStoreSession.activityLogEntryStore.load({ tableName: Constants.compensationTableName });
            }

            await rootStoreSession.municipalityStore.loadAll();
            await rootStoreSession.contactStore.loadAll();
            await rootStoreSession.userStore.loadAll();

            appStore.setProjectId(projectCategory, projectId, true);

            return { project, appStore };
        } catch (e) {
            throw new Response("", {
                status: e.statusCode,
                statusText: e.message,
            });
        }
    }

    throw new Response("", {
        status: 500,
        statusText: "Fehler beim Laden des Projekts!",
    });
}

export async function action(request: Request, params: Params, rootStore: RootStore) {
    if (params.projectId !== undefined) {
        const formData = await request.formData();
        const compensationApproved = formData.get("compensationApproved") === "true";
        const projectId = parseInt(params.projectId) || 0;
        try {
            const rootStoreSession = rootStore.session;
            return await rootStoreSession.projectStore.patchUsageProject(projectId, compensationApproved);
        } catch (e: TransportError) {
            return null;
        }
    }

    return null;
}

export default function ProjectShow(): JSX.Element {
    const { project, appStore } = useLoaderData() as LoadResult;
    const submit = useSubmit();
    const fetcher = useFetcher();
    const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const usageProject: UsageProject | null = useActionData() as any;
    const compensationApproved = usageProject?.compensationApproved ?? (project as UsageProject).compensationApproved;

    if (deleting) {
        return (
            <Dimmer active={true} page={true}>
                <Loader>Löschen</Loader>
            </Dimmer>
        );
    }

    return (
        <div className="projectShow">
            <div>
                <Breadcrumb size="big">
                    <BreadcrumbSection><Link to="/projekte">Projekte</Link></BreadcrumbSection>
                    <BreadcrumbDivider/>
                    <BreadcrumbSection>{project.fullType}</BreadcrumbSection>
                    <BreadcrumbDivider/>
                    <BreadcrumbSection>ID {project.id}</BreadcrumbSection>
                </Breadcrumb>
                <Header>{project.name}</Header>
                <div className="icons">
                    <ActionStatusIcon actionStatus={project.actionStatus} issues={project.issues}/>
                    <span onClick={() => {
                        appStore.clearProjectId();
                        appStore.setProjectId(project.categoryKey, project.id, true);
                        window.scrollTo({ top: 0, behavior: "instant" });
                    }}>
                        <SVG src={locateIcon} height="auto" width={25} title="Fokus"/>
                    </span>
                </div>
            </div>
            <div>
                {project.typeKey === Constants.projectTypeConstructionPermit &&
                    <ConstructionPermitShow project={project as ConstructionPermitProject}/>}
                {project.typeKey === Constants.projectTypeLandUsePlanChange &&
                    <LandUsePlanChangeShow project={project as LandUsePlanChangeProject}/>}
                {project.typeKey === Constants.projectTypeLandfillSite &&
                    <LandfillSiteShow project={project as LandfillSiteProject}/>}
                {project.typeKey === Constants.projectTypeSoilImprovement &&
                    <SoilImprovementShow project={project as SoilImprovementProject}/>}
                {project.typeKey === Constants.projectTypeDezoning &&
                    <DezoningShow project={project as DezoningProject}/>}

                <Grid columns={2} relaxed={true}>
                    <GridColumn>
                        <Link to={`/projekte/${project.categoryKey}/${project.id}/aendern`}>
                            <Button type="button" size="mini">Bearbeiten</Button>
                        </Link>
                    </GridColumn>
                    <GridColumn>
                    </GridColumn>
                </Grid>
            </div>
            <div>
                <Header as="h2">Flächenbilanz [m²]</Header>
                {project.categoryKey === Constants.projectCategoryCreation &&
                    <AreaDataCreationProject project={project as CreationProject} appStore={appStore}/>}
                {project.categoryKey === Constants.projectCategoryUsage &&
                    project.typeKey !== Constants.projectTypeLandfillSite &&
                    <AreaDataUsageProject project={project as UsageProject} appStore={appStore}/>}
                {project.categoryKey === Constants.projectCategoryUsage &&
                    project.typeKey === Constants.projectTypeLandfillSite &&
                    <AreaDataLandfillSite project={project as LandfillSiteProject} appStore={appStore}/>}
                {project.categoryKey === Constants.projectCategoryUsage &&
                    <Grid columns={1}>
                        <GridColumn textAlign="right">
                            <fetcher.Form method="post" id="approveCompensationForm">
                                <div className="approveCompensation">
                                    <input id="checkbox"
                                           type="checkbox"
                                           name="compensationApproved"
                                           checked={compensationApproved}
                                           value={!compensationApproved}
                                           onChange={() => submit(document.getElementById("approveCompensationForm") as HTMLFormElement)}/>
                                    <label for="checkbox">Kompensation genehmigen</label>
                                </div>
                            </fetcher.Form>
                        </GridColumn>
                    </Grid>}
            </div>
            <div>
                <Grid columns={1} relaxed={true}>
                    <GridColumn>
                        <div className="buttons">
                            <Link to={"/projekte"}>
                                <Button type="button"> Zurück </Button>
                            </Link>
                            <Form
                                id="deleteProjectForm"
                                method="delete"
                                action={`/projekte/${project.categoryKey}/${project.id}/loeschen`}
                            >
                                <Modal
                                    size="mini"
                                    onClose={() => setShowConfirmDeleteDialog(false)}
                                    onOpen={() => setShowConfirmDeleteDialog(true)}
                                    open={showConfirmDeleteDialog}
                                    trigger={
                                        (project.compensations.length === 0 || project.typeKey === Constants.projectTypeLandfillSite) &&
                                        <a href="#" onClick={(e) => {
                                            e.preventDefault();
                                            setShowConfirmDeleteDialog(true);
                                        }}>Projekt löschen</a>
                                    }
                                    dimmer="blurring"
                                >
                                    <Modal.Content>
                                        Projekt "{project.name}" wirklich löschen?
                                    </Modal.Content>
                                    <Modal.Actions>
                                        <Button onClick={() => setShowConfirmDeleteDialog(false)}>
                                            Abbrechen
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                setShowConfirmDeleteDialog(false);
                                                setDeleting(true);
                                                submit(document.getElementById("deleteProjectForm") as HTMLFormElement);
                                            }}
                                        >
                                            Löschen
                                        </Button>
                                    </Modal.Actions>
                                </Modal>
                            </Form>
                        </div>
                    </GridColumn>
                </Grid>
                <br/>
                <span style={{ fontSize: "12px" }}>
                    {getLabel(CreationProject.prototype, "updatedAt")}: {project.updatedAt} {project.updatedBy.fullName}
                    </span>
            </div>
            <Outlet/>
        </div>
    );
}
